import { openCurrentModal, closeCurrentModal } from 'redux/slices/modals/modals.slice';
import { setEmailVerified, setIsAuth } from 'redux/slices/app/app.slice';
import { EModalsNames } from 'shared/types/modal.interface';
import { LOGIN_MODAL_DATA } from 'shared/constants/modal-constants';
import { AUTH_FETCHES_SUCCESS, PROFILE_FETCHES_SUCCESS } from 'shared/constants/alert-constants';
import { handleSnackbarNotify } from 'utils/handleSnackbarNotify';
import { handleCatchNotValidationError } from 'utils/handleCatchNotValidationError';
import {
  RegistrationRequest,
  RegistrationResponse,
  EmailVerifyRequest,
  TokenRequest,
  TokenResponse,
  LogoutResponse,
  EReduxQueryTags,
  EmailVerifyResponse,
  ForgotPasswordRequest,
  ConfirmPasswordRequest,
  ChangePasswordRequest,
} from 'shared/types/api.interface';
import { queryUrls } from 'shared/constants/queryApiUrl-constants';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { splitApi } from './split.api';

export const AuthApi = splitApi.injectEndpoints({
  endpoints: (build) => ({
    registration: build.mutation<RegistrationResponse, RegistrationRequest>({
      query: (body) => ({
        url: queryUrls.authApi.registration,
        method: 'POST',
        body,
      }),
    }),
    emailVerify: build.query<EmailVerifyResponse, EmailVerifyRequest>({
      query: (data) => ({
        url: `${queryUrls.authApi.emailVerify}/${data.uidb64}/${data.token}/`,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            openCurrentModal({
              popupName: EModalsNames.LoginModal,
              data: { title: LOGIN_MODAL_DATA.titleAfterRegistration },
            }),
          );
        } catch (err) {
          dispatch(setEmailVerified(false));
          // проверить необходимость этого стейта,
          // мб можно на теги заменить, если за ним идет какой-то запрос
          handleSnackbarNotify(dispatch, err.error.data.errors[0].detail, 'error');
        }
      },
    }),
    login: build.mutation<TokenResponse, TokenRequest>({
      query: (body) => ({ url: queryUrls.authApi.login, method: 'POST', body }),
      invalidatesTags: (result) => {
        if (result) {
          localStorage.setItem(LOCAL_STORAGE.token, result.access);
          localStorage.setItem(LOCAL_STORAGE.refreshToken, result.refresh);
          return [EReduxQueryTags.User];
        }
        return [];
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          handleSnackbarNotify(dispatch, AUTH_FETCHES_SUCCESS.fetchAuth, 'success');
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    logout: build.mutation<LogoutResponse, void>({
      query: () => {
        const refresh = localStorage.getItem(LOCAL_STORAGE.refreshToken);
        return { url: queryUrls.authApi.logout, method: 'POST', body: { refresh } };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setIsAuth(false));
          localStorage.clear(); // delete all tokens
          dispatch(AuthApi.util.resetApiState()); // reset cash
          handleSnackbarNotify(dispatch, AUTH_FETCHES_SUCCESS.fetchLogout, 'success');
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    forgotPassword: build.mutation<any, ForgotPasswordRequest>({
      query: (body) => ({ url: queryUrls.authApi.forgotPassword, method: 'post', body }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            openCurrentModal({
              popupName: EModalsNames.RestorePasswordModal,
              data: { type: 1 },
            }),
          );
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    resetPassword: build.mutation<any, ConfirmPasswordRequest>({
      query: (body) => {
        const { new_password, confirm_new_password, uidb64, token } = body;
        return {
          url: `${queryUrls.authApi.resetPassword}/${uidb64}/${token}/`,
          method: 'POST',
          body: { new_password, confirm_new_password },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            openCurrentModal({
              popupName: EModalsNames.LoginModal,
              data: { title: LOGIN_MODAL_DATA.titleAfterRestorePassword },
            }),
          );
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    changePassword: build.mutation<any, ChangePasswordRequest>({
      query: (body) => ({
        url: queryUrls.authApi.changePassword,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          handleSnackbarNotify(dispatch, PROFILE_FETCHES_SUCCESS.fetchChangePassword, 'success');
          dispatch(closeCurrentModal());
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useRegistrationMutation,
  useEmailVerifyQuery,
  useLoginMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
} = AuthApi;
